import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface DigitalClockProps {
  label: string;
  /** Segundos. `null` → «--:--». */
  seconds: number | null;
  onChange: (seconds: number) => void;
  /** Por defecto 15 s. */
  min?: number;
  /** Por defecto 1 200 s (20 min). */
  max?: number;
  /** Por defecto 60 s. */
  minuteStep?: number;
  /** Por defecto 15 s. */
  secondStep?: number;
  labels: { minuteUp: string; minuteDown: string; secondUp: string; secondDown: string };
  /** Rótulo pequeño junto a la cifra («MIN:SEC»). */
  caption: string;
  /** Texto que lee el lector de pantalla: «2 minutes 0 seconds…». */
  valueText: (minutes: number, seconds: number) => string;
  hint?: ReactNode;
  className?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

const arrowCls =
  'inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[var(--color-bg-elevated)] text-sm font-bold text-[var(--color-accent-ink)] transition-colors hover:bg-[var(--color-border-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-ink)] disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transition-none';

/**
 * Reloj digital MM:SS sobre panel oscuro, con ▲▼ para los minutos (izquierda) y los segundos (derecha).
 * Un valor fuera del rango se lee tal cual; sólo cambia cuando el usuario toca una flecha. Sin paso a la
 * rejilla: 1:42 + 15 s = 1:57.
 */
export function DigitalClock({
  label,
  seconds,
  onChange,
  min = 15,
  max = 1200,
  minuteStep = 60,
  secondStep = 15,
  labels,
  caption,
  valueText,
  hint,
  className,
}: DigitalClockProps) {
  const labelId = useId();
  const known = seconds != null && Number.isFinite(seconds);
  const total = known ? Math.max(0, Math.round(seconds)) : 0;
  const m = Math.floor(total / 60);
  const s = total % 60;
  const text = known ? `${pad(m)}:${pad(s)}` : '--:--';
  // Desde «sin dato», ▲ arranca en 2 min.
  const move = (delta: number) => onChange(known ? Math.max(min, Math.min(max, total + delta)) : 120);
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <span id={labelId} className="text-sm font-medium text-[var(--color-fg-primary)]">{label}</span>
      <div
        role="group"
        aria-labelledby={labelId}
        data-surface="night"
        className="flex items-center justify-between gap-3 rounded-2xl bg-[var(--color-xk-bg-nav)] px-4 py-3.5"
      >
        <div className="flex flex-col gap-1.5">
          <button type="button" aria-label={labels.minuteUp} onClick={() => move(minuteStep)} disabled={known && total >= max} className={arrowCls}>
            ▲
          </button>
          <button type="button" aria-label={labels.minuteDown} onClick={() => move(-minuteStep)} disabled={!known || total <= min} className={arrowCls}>
            ▼
          </button>
        </div>
        <div className="flex items-baseline gap-1.5">
          <output
            aria-live="polite"
            aria-label={known ? valueText(m, s) : undefined}
            className="font-[var(--font-dash-mono)] text-[56px] font-bold leading-none tracking-[4px] text-[var(--color-accent-ink)] [text-shadow:0_0_14px_color-mix(in_srgb,var(--color-xk-accent)_45%,transparent)]"
          >
            {text}
          </output>
          <span aria-hidden="true" className="text-[11px] font-medium tracking-[1px] text-[var(--color-fg-muted)]">
            {caption}
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <button type="button" aria-label={labels.secondUp} onClick={() => move(secondStep)} disabled={known && total >= max} className={arrowCls}>
            ▲
          </button>
          <button type="button" aria-label={labels.secondDown} onClick={() => move(-secondStep)} disabled={!known || total <= min} className={arrowCls}>
            ▼
          </button>
        </div>
      </div>
      {hint ? <span className="text-xs text-[var(--color-fg-muted)]">{hint}</span> : null}
    </div>
  );
}
